import {
  type CanvasDrawingServiceInitOptions,
  type DrawCircleOptions,
  type DrawEllipsisShadowOptions,
  type DrawGlowOptions,
  type DrawGlowPulseOptions,
  type DrawShadowOptions,
  type DrawTextInCoordinatesOptions,
  type ICanvasDrawingService,
} from '../ICanvasDrawingService';

/**
 * @todo: подумать, м.б. класс со статическими методами будет здесь выгоднее
 */
export class CanvasDrawingService implements ICanvasDrawingService {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly dpr: number;

  public constructor(options: CanvasDrawingServiceInitOptions) {
    this.ctx = options.context;
    this.dpr = options.dpr;
  }

  public drawCircle = (opts: DrawCircleOptions): void => {
    const { pinCoords, radius, color } = opts;
    const [pointX, pointY] = this.getPinCoords(pinCoords);

    this.ctx.beginPath();

    this.ctx.fillStyle = color;
    this.ctx.arc(pointX * this.dpr, pointY * this.dpr, radius * this.dpr, 0, 2 * Math.PI);
    this.ctx.fill();

    this.ctx.closePath();
  };

  public drawTextInCoordinates = (opts: DrawTextInCoordinatesOptions): void => {
    const { pinCoords, color, text } = opts;

    if (text) {
      const [pointX, pointY] = this.getPinCoords(pinCoords);

      this.ctx.beginPath();

      this.ctx.font = `${8.5 * this.dpr}px/1 Lato`;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'alphabetic';
      this.ctx.fillStyle = color;
      this.ctx.fillText(text, pointX * this.dpr, (pointY + 4) * this.dpr);

      this.ctx.closePath();
    }
  };

  public drawShadow = (opts: DrawShadowOptions): void => {
    const { shadowColor = '', shadowBlur = 0, shadowOffsetY = 0, shadowOffsetX = 0 } = opts;

    this.ctx.shadowColor = shadowColor;
    this.ctx.shadowBlur = shadowBlur;
    this.ctx.shadowOffsetY = shadowOffsetY;
    this.ctx.shadowOffsetX = shadowOffsetX;
  };

  public drawEllipsisShadow(opts: DrawEllipsisShadowOptions): void {
    const { pinCoords, radius, color } = opts;
    const [pointX, pointY] = this.getPinCoords(pinCoords);

    const radiusX = radius * 0.6;

    this.ctx.beginPath();
    this.ctx.fillStyle = color;
    this.ctx.ellipse(
      pointX * this.dpr,
      (pointY + radiusX + 1) * this.dpr,
      radius * 1.3 * this.dpr,
      radiusX * this.dpr,
      0,
      0,
      2 * Math.PI,
    );
    this.ctx.fill();
    this.ctx.closePath();
  }

  public drawGlow = (opts: DrawGlowOptions): void => {
    const { pinCoords, glowRadius, color } = opts;
    const [pointX, pointY] = this.getPinCoords(pinCoords);

    this.ctx.save();
    this.ctx.globalAlpha = 0.4;
    this.ctx.beginPath();
    this.ctx.fillStyle = color;
    this.ctx.arc(pointX * this.dpr, pointY * this.dpr, glowRadius * this.dpr, 0, 2 * Math.PI);
    this.ctx.fill();
    this.ctx.closePath();
    this.ctx.restore();
  };

  public drawGlowPulse = (opts: DrawGlowPulseOptions): void => {
    const { radius, color, pulseProgress, pinCoords } = opts;
    const [pointX, pointY] = this.getPinCoords(pinCoords);

    /** Коэффициент устройства, используется для корректной отрисовки на Retina экранах */
    const dpr = this.dpr;
    /** Максимальный радиус пульсации, который будет равен (radius + MAX_PULSE_RADIUS) * dpr */
    const maxPulse = (radius + opts.pulseRadius) * dpr;
    /** Минимальный радиус пульсации, который будет равен (radius + MIN_PULSE_RADIUS) * dpr */
    const minPulse = (radius + 2) * dpr;
    /** Радиус пульсации: треугольная волна — от минимума к максимуму и обратно */
    const triangleProgress = pulseProgress < 0.5 ? pulseProgress * 2 : (1 - pulseProgress) * 2;
    const pulseRadius = minPulse + (maxPulse - minPulse) * triangleProgress;
    /** Прозрачность круга изменяется от 0.2 (min) до 0.4 (max) синхронно с радиусом */
    const alpha = 0.2 + 0.2 * triangleProgress;

    this.ctx.save();
    this.ctx.beginPath();
    /**
     * Рисуем круг с центром в точке (pointX, pointY + 0.3) и радиусом pulseRadius
     * 0.3 - это смещение по Y, чтобы пульсация была ровно по центру точки
     */
    this.ctx.arc(pointX * dpr, (pointY - 0.3) * dpr, pulseRadius, 0, 2 * Math.PI);
    /** Заливаем круг цветом с прозрачностью alpha */
    this.ctx.fillStyle = `rgb(${color}, ${alpha.toFixed(2)})`;
    this.ctx.fill();
    this.ctx.closePath();
    this.ctx.restore();
  };

  public clearRect = (x: number, y: number, w: number, h: number): void => {
    this.ctx.clearRect(x, y, w, h);
  };

  private getPinCoords(pinCoords: [number, number]): [number, number] {
    const [pointXRaw, pointYRaw] = pinCoords;

    return [Math.round(pointXRaw), Math.floor(pointYRaw)];
  }
}
